import { useQuery } from '@tanstack/react-query';
import { Query } from './api';

// After Query.run() processes the response, Model objects have attributes
// flattened as top-level properties (e.g. model.entitlement_type, not
// model.attributes.entitlement_type).

export interface EntitlementModel {
  id: string;
  entitlement_type: 'feature' | 'limit';
  entitlement_key: string;
  value: string | null;
  current?: number;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

export const useAccountEntitlements = (accountId: string) => {
  return useQuery({
    queryKey: [accountId, 'entitlements'],
    queryFn: async () => {
      return await new Query(`accounts/${accountId}/entitlements`)
        .size(1000)
        .run();
    },
    staleTime: 300000,
    enabled: Boolean(accountId),
  });
};

export const hasFeature = (
  entitlements: EntitlementModel[] | undefined,
  key: string,
): boolean => {
  if (!entitlements) return false;
  return entitlements.some(
    (e) => e.entitlement_type === 'feature' && e.entitlement_key === key,
  );
};

export const hasAccess = (
  entitlements: EntitlementModel[] | undefined,
  entityType: string,
  name: string,
): boolean => {
  const fullKey = `${entityType}:${name}`;
  const limitInfo = getLimit(entitlements, fullKey);
  return limitInfo !== null && limitInfo.limit >= 1;
};

export const getLimit = (
  entitlements: EntitlementModel[] | undefined,
  key: string,
): { limit: number; current: number } | null => {
  if (!entitlements) return null;
  const ent = entitlements.find(
    (e) => e.entitlement_type === 'limit' && e.entitlement_key === key,
  );
  if (!ent || ent.value === null) return null;
  return {
    limit: parseInt(ent.value, 10),
    current: ent.current ?? 0,
  };
};
